import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select, Textarea } from 'components/common';
import { getAdminProjectInformationTypes } from 'apis/adminProjectInformationTypes';
import { getAdminTypicalProjects } from 'apis/adminTypicalProjects';
import {
    createTypicalProjectInformationItem,
    getAdminTypicalProjectInformationItemById,
    getAdminTypicalProjectInformationItems,
    updateTypicalProjectInformationItem,
} from 'apis/adminTypicalProjectInformationItems';
import { normalizeUploadedImageUrl } from 'apis/uploadImage';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminListQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const getTypeName = (type) => type?.name || type?.label || type?.translations?.[0]?.name || type?.code || '';
const getProjectTitle = (project) => project?.title || project?.translations?.[0]?.title || `Dự án ${project?.id}`;

const createInfoItem = (languages, sortOrder = 0) => ({
    projectInformationTypeId: '',
    sortOrder,
    isActive: true,
    translation: languages.map((language) => ({ languageId: language.id, value: '' })),
});

const TypicalProjectInformationItemsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [activeTab, setActiveTab] = useState(0);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        typicalProjectId: '',
        items: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: projectData = { items: [] } } = useAdminListQuery(
        'typical-projects-options',
        { pageSize: 1000, languageCode: 'vi' },
        getAdminTypicalProjects,
    );
    const { data: typeData = { items: [] } } = useAdminListQuery(
        'project-information-types-options',
        { pageSize: 1000, languageCode: 'vi', isActive: true },
        getAdminProjectInformationTypes,
    );
    const projects = projectData.items;
    const types = typeData.items;
    const { data: detail, error: detailError } = useAdminDetailQuery(
        'typical-project-information-items',
        id,
        getAdminTypicalProjectInformationItemById,
    );
    const createMutation = useAdminCreateMutation('typical-project-information-items', createTypicalProjectInformationItem);
    const updateMutation = useAdminUpdateMutation('typical-project-information-items', updateTypicalProjectInformationItem);
    const loading = createMutation.isPending || updateMutation.isPending;
    const isEditMode = Boolean(id);

    useEffect(() => {
        if (!id && languages.length && !formData.items.length) {
            setFormData((prev) => ({
                ...prev,
                items: [createInfoItem(languages, 0)],
            }));
        }
    }, [id, languages, formData.items.length]);

    useEffect(() => {
        if (!detail) return;
        setFormData({
            typicalProjectId: detail.typicalProjectId || '',
            items: [
                {
                    projectInformationTypeId: detail.projectInformationTypeId || '',
                    sortOrder: detail.sortOrder || 0,
                    isActive: detail.isActive ?? true,
                    translation: detail.translation || detail.translations || [],
                },
            ],
        });
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const getNextSortOrder = async (typicalProjectId) => {
        if (!typicalProjectId) return 0;
        const response = await getAdminTypicalProjectInformationItems({
            pageIndex: 1,
            pageSize: 1000,
            typicalProjectId,
            languageCode: 'vi',
        });
        const items = Array.isArray(response?.data) ? response.data : response?.data?.items || [];
        const maxSortOrder = items.reduce((max, item) => {
            if (Number(item.id) === Number(id)) return max;
            return Math.max(max, Number(item.sortOrder) || 0);
        }, 0);
        return maxSortOrder + 1;
    };

    const handleProjectChange = async (value) => {
        setFormData((prev) => ({
            ...prev,
            typicalProjectId: value,
            items: prev.items.map((item) => ({ ...item, sortOrder: value ? item.sortOrder : 0 })),
        }));

        if (!value) return;

        try {
            const nextSortOrder = await getNextSortOrder(value);
            setFormData((prev) => ({
                ...prev,
                items: prev.items.map((item, index) => ({
                    ...item,
                    sortOrder: nextSortOrder + index,
                })),
            }));
        } catch {
            toast.error('Không lấy được thứ tự tự động');
        }
    };

    const handleItemChange = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            items: prev.items.map((item, itemIndex) =>
                itemIndex === index ? { ...item, [field]: value } : item,
            ),
        }));
    };

    const handleTranslationChange = (itemIndex, languageId, value) => {
        setFormData((prev) => ({
            ...prev,
            items: prev.items.map((item, index) => {
                if (index !== itemIndex) return item;

                const hasTranslation = item.translation.some(
                    (translation) => Number(translation.languageId) === Number(languageId),
                );

                return {
                    ...item,
                    translation: hasTranslation
                        ? item.translation.map((translation) =>
                            Number(translation.languageId) === Number(languageId)
                                ? { ...translation, value }
                                : translation,
                        )
                        : [...item.translation, { languageId, value }],
                };
            }),
        }));
    };

    const handleAddItem = () => {
        setFormData((prev) => {
            const maxSortOrder = prev.items.reduce(
                (max, item) => Math.max(max, Number(item.sortOrder) || 0),
                0,
            );

            return {
                ...prev,
                items: [...prev.items, createInfoItem(languages, maxSortOrder + 1)],
            };
        });
    };

    const handleRemoveItem = (index) => {
        setFormData((prev) => ({
            ...prev,
            items: prev.items.length > 1
                ? prev.items.filter((_, itemIndex) => itemIndex !== index)
                : prev.items,
        }));
    };

    const validate = () => {
        const nextErrors = {};
        if (!formData.typicalProjectId) nextErrors.typicalProjectId = 'Vui lòng chọn dự án';
        if (!formData.items.length) nextErrors.items = 'Vui lòng thêm ít nhất một thông tin';

        formData.items.forEach((item, index) => {
            if (!item.projectInformationTypeId) {
                nextErrors[`items.${index}.projectInformationTypeId`] = 'Vui lòng chọn loại thông tin';
            }
            if (!item.translation.some((translation) => translation.value?.trim())) {
                nextErrors[`items.${index}.translation`] = 'Vui lòng nhập giá trị cho ít nhất một ngôn ngữ';
            }
        });

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const buildPayload = (item) => ({
        typicalProjectId: Number(formData.typicalProjectId),
        projectInformationTypeId: Number(item.projectInformationTypeId),
        sortOrder: Number(item.sortOrder) || 0,
        isActive: Boolean(item.isActive),
        translation: item.translation.map((translation) => ({
            languageId: Number(translation.languageId),
            value: translation.value || '',
        })),
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            if (isEditMode) {
                const res = await updateMutation.mutateAsync({
                    id,
                    data: buildPayload(formData.items[0]),
                });
                if (!res.success) {
                    if (res.messageError) setErrors(res.messageError);
                    else toast.error(res.message || 'Lưu thất bại');
                    return;
                }
            } else {
                const payload = formData.items.map((item) => buildPayload(item));
                const res = await createMutation.mutateAsync(payload);
                if (!res.success) {
                    if (res.messageError) setErrors(res.messageError);
                    else toast.error(res.message || 'Lưu thất bại');
                    return;
                }
            }

            toast.success(isEditMode ? 'Cập nhật thành công' : 'Thêm mới thành công');
            navigate('/admin/typical-project-information-items');
        } catch {
            toast.error('Lưu thất bại');
        }
    };

    const activeLanguage = languages[activeTab];

    return (
        <div className="admin-page">
            <PageHeader title={isEditMode ? 'Cập nhật thông tin dự án' : 'Thêm thông tin dự án'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Dự án sử dụng thông tin</p>
                <Select
                    label="Dự án"
                    name="typicalProjectId"
                    value={formData.typicalProjectId}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    options={projects.map((project) => ({ value: project.id, label: getProjectTitle(project) }))}
                    placeholder="Chọn dự án"
                    error={errors.typicalProjectId}
                    required
                />

                <p className="form-section-title">Danh sách thông tin</p>
                {errors.items && <p className="error-message" style={{ color: '#dc2626', fontSize: 13 }}>{errors.items}</p>}
                <div className="translation-tabs">
                    <div className="tab-header">
                        {languages.map((language, index) => (
                            <button key={language.id} type="button" className={`tab-btn ${activeTab === index ? 'active' : ''}`} onClick={() => setActiveTab(index)}>
                                {language.name} ({language.code})
                            </button>
                        ))}
                    </div>
                </div>

                {formData.items.map((item, index) => {
                    const selectedType = types.find((type) => Number(type.id) === Number(item.projectInformationTypeId));
                    const selectedIcon = normalizeUploadedImageUrl(selectedType?.iconURL || selectedType?.iconUrl || '');
                    const activeTranslation = item.translation.find((translation) => Number(translation.languageId) === Number(activeLanguage?.id));

                    return (
                        <div key={index} className="admin-nested-block" style={{ marginBottom: 22, paddingBottom: 22, borderBottom: '1px solid #cfe8d7' }}>
                            <div className="form-row" style={{ alignItems: 'flex-end' }}>
                                <Select
                                    label="Loại thông tin *"
                                    value={item.projectInformationTypeId}
                                    onChange={(e) => handleItemChange(index, 'projectInformationTypeId', e.target.value)}
                                    options={types.map((type) => ({ value: type.id, label: getTypeName(type) }))}
                                    placeholder="Chọn loại thông tin"
                                    error={errors[`items.${index}.projectInformationTypeId`]}
                                    required
                                />
                                <Input
                                    label="Thứ tự"
                                    type="number"
                                    value={item.sortOrder}
                                    onChange={(e) => handleItemChange(index, 'sortOrder', e.target.value)}
                                />
                                <Checkbox
                                    label="Hoạt động"
                                    checked={item.isActive}
                                    onChange={(e) => handleItemChange(index, 'isActive', e.target.checked)}
                                />
                                {!isEditMode && (
                                    <Button
                                        type="button"
                                        variant="danger"
                                        onClick={() => handleRemoveItem(index)}
                                        disabled={formData.items.length === 1}
                                    >
                                        Xóa
                                    </Button>
                                )}
                            </div>
                            {selectedType && (
                                <div className="field-help" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                    {selectedIcon && <img src={selectedIcon} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />}
                                    <span>{selectedType.code}</span>
                                </div>
                            )}
                            {activeLanguage && (
                                <Textarea
                                    label={`Giá trị (${activeLanguage.name})`}
                                    value={activeTranslation?.value || ''}
                                    onChange={(e) => handleTranslationChange(index, activeLanguage.id, e.target.value)}
                                    error={errors[`items.${index}.translation`]}
                                    rows={3}
                                    required
                                />
                            )}
                        </div>
                    );
                })}

                {!isEditMode && (
                    <div style={{ marginBottom: 20 }}>
                        <Button type="button" variant="secondary" onClick={handleAddItem}>
                            + Thêm Thông Tin
                        </Button>
                    </div>
                )}

                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : 'Lưu'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/typical-project-information-items')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default TypicalProjectInformationItemsForm;
